// @ts-nocheck — pre-existing module, not in the codetab checkJs surface.
// gitCheckpoint.js — wire-dead-engines E4-WS1-T01 (+ audit fix: orphan untracked cleanup).
// @arch:pure-engine
//
// A NON-DESTRUCTIVE, renderer-side checkpoint for the live DAG executor (Cursor
// "iterate-and-restore"). taskExecutor.runTaskGraph already calls injected
// saveCheckpoint(task)→id on honest-green and restoreCheckpoint(id) on a fail; these
// seams were never filled. This fills them with git, the cheapest real restore boundary.
//
// SAFE BY DESIGN (this runs autonomously on the user's real repo):
//   save():     `git stash create` snapshots the working tree into a dangling commit
//               WITHOUT touching the tree or the stash list. Clean tree ⇒ falls back to HEAD.
//               ALSO records the set of untracked files present at the snapshot (the baseline).
//   restore(id):`git checkout <id> -- .` restores TRACKED files to the snapshot. Then removes
//               ONLY the untracked files CREATED SINCE this checkpoint (current untracked minus
//               the baseline) via a PATH-SCOPED `git clean -f -- <exact paths>`. It never touches
//               tracked files, never `reset --hard`, and never removes a pre-existing untracked
//               file the user already had — only orphans a failed task left behind. (Audit gap:
//               `git stash create` snapshots tracked changes only, so without this the N-1 restore
//               left orphan untracked files behind, contradicting the executor's clean-tree claim.)
//
// PURE (Tier 1 lib purity): no react/window. The shell port (runCommand) + root injected.

export function makeGitCheckpoint({ root, runCommand } = {}) {
  // id → Set<path> of untracked files present when that checkpoint was saved.
  const untrackedBaselines = new Map();

  const run = async (command) => {
    if (typeof runCommand !== 'function') return { exitCode: null, stdout: '', stderr: 'no runCommand' };
    try {
      return (await runCommand({ command, cwd: root })) || {};
    } catch (e) {
      return { exitCode: null, stdout: '', stderr: e?.message || String(e) };
    }
  };

  // Set of currently-untracked paths. Quoted/special-char paths (git wraps them in "") are
  // SKIPPED from the cleanup set — they simply survive, exactly as before (no regression, never
  // a wrong removal).
  const snapshotUntracked = async () => {
    const r = await run('git status --porcelain --untracked-files=all');
    const set = new Set();
    for (const line of String(r?.stdout || '').split('\n')) {
      if (line.startsWith('?? ')) {
        const p = line.slice(3).trim();
        if (p && !p.startsWith('"')) set.add(p); // skip quoted (special-char) paths — leave them
      }
    }
    return set;
  };

  return {
    /** Snapshot the current (post-green) tree → a restorable id. Never mutates the tree. */
    async save() {
      const created = await run('git stash create "apolo-checkpoint"');
      const sha = String(created?.stdout || '').trim();
      const id = sha || String((await run('git rev-parse HEAD'))?.stdout || '').trim() || null;
      if (id) untrackedBaselines.set(id, await snapshotUntracked());
      return id;
    },

    /**
     * Restore TRACKED files to the snapshot id, then remove ONLY untracked orphans created since
     * that checkpoint (path-scoped). Best-effort + non-destructive to anything that predates it.
     */
    async restore(id) {
      if (!id) return false;
      const res = await run(`git checkout ${id} -- .`);
      const ok = res?.exitCode === 0;
      const baseline = untrackedBaselines.get(id);
      // Only sweep orphans when the restore actually happened — if checkout failed, leave the tree be.
      if (ok && baseline) {
        const now = await snapshotUntracked();
        const orphans = [...now].filter((p) => !baseline.has(p));
        if (orphans.length) {
          // Path-scoped: removes EXACTLY these untracked orphans, nothing else.
          const paths = orphans.map((p) => JSON.stringify(p)).join(' ');
          await run(`git clean -f -- ${paths}`);
        }
      }
      return ok;
    },
  };
}
